/**
 * Hooks API Formateurs (Rafik 03/06/2026).
 *
 * GET liste, CREATE et UPDATE via /v2/formateurs.
 * Le DTO merge Zoho (basic) + cockpit BDD FormateurExtra (admin).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';

export interface FormateurFull {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  tarifHoraire: number;
  specialites: string[];
  roles: string[];
  statut: string;
  typeNDA: string;
  // Extra cockpit
  statutJuridique: string | null;
  siret: string | null;
  nda: string | null;
  adresse: string | null;
}

export interface CreateFormateurInput {
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  tarifHoraire?: number;
  specialites?: string[];
  roles?: string[];
  statut?: string;
  typeNDA?: string;
  statutJuridique?: string;
  siret?: string;
  nda?: string;
  adresse?: string;
}

export type UpdateFormateurInput = Partial<CreateFormateurInput>;

const QK_LIST = ['formateurs', 'list'] as const;

export function useFormateurs() {
  return useQuery({
    queryKey: QK_LIST,
    queryFn: () => api.get<{ formateurs: FormateurFull[] }>('/api/v1/v2/formateurs'),
    staleTime: 30_000,
  });
}

export function useCreateFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFormateurInput) =>
      api.post<FormateurFull>('/api/v1/v2/formateurs', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST });
      qc.invalidateQueries({ queryKey: ['contrats-formateur'] });
    },
  });
}

export function useUpdateFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateFormateurInput }) =>
      api.patch<FormateurFull>(`/api/v1/v2/formateurs/${input.id}`, input.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST });
      qc.invalidateQueries({ queryKey: ['contrats-formateur'] });
    },
  });
}
